import { z } from 'zod';

import { type BaseItem, type NullablePartial } from './common.js';
import { isDefined } from '../utils.js';

// Node type enum for better type safety
export enum DecisionTreeNodeType {
  Decision = 'decision',
  Action = 'action',
  Rule = 'rule',
  DataCollection = 'data_collection',
  End = 'end',
}

// Zod schemas for decision tree validation
export const DecisionOptionSchema = z.object({
  text: z.string().min(1, 'Option text is required and cannot be empty'),
  nextNodeId: z.string().min(1, 'Next node ID is required and cannot be empty'),
});

export const DecisionTreeNodeSchema = z.object({
  id: z.string().min(1, 'Node ID is required and cannot be empty'),
  type: z.literal(DecisionTreeNodeType.Decision, {
    errorMap: () => ({ message: 'Decision nodes must have type "decision"' }),
  }),
  question: z.string().min(1, 'Question is required and cannot be empty'),
  options: z.array(DecisionOptionSchema).min(1, 'Decision nodes must have at least one option'),
  ehrQuestionId: z.string().optional(),
});
export type DecisionNode = z.infer<typeof DecisionTreeNodeSchema>;

export const DecisionTreeActionNodeSchema = z.object({
  id: z.string().min(1, 'Node ID is required and cannot be empty'),
  type: z.literal(DecisionTreeNodeType.Action, {
    errorMap: () => ({ message: 'Action nodes must have type "action"' }),
  }),
  actionType: z.enum(['schedule', 'transfer'], {
    errorMap: () => ({ message: 'Action type must be either "schedule" or "transfer"' }),
  }),
  instruction: z.string().min(1, 'Instruction is required and cannot be empty'),
  practiceIds: z.array(z.string()).optional(),
  providerUserIds: z.array(z.string()).optional(),
  appointmentTypes: z.array(z.string()).optional(),
});
export type DecisionTreeNodeAction = z.infer<typeof DecisionTreeActionNodeSchema>;

// Define the base rule operation schema without recursion
const BaseRuleOperationSchema = z.object({
  field: z.string().min(1, 'Field is required and cannot be empty'),
  value: z.string().min(1, 'Value is required and cannot be empty'),
  operator: z.string().min(1, 'Operator is required and cannot be empty'),
  previousToolCallResultNodeId: z
    .string()
    .min(1, 'Previous tool call result node ID is required and cannot be empty'),
});

// Create the recursive schema
export const RuleOperationSchema: z.ZodType = BaseRuleOperationSchema.extend({
  ruleOperations: z.array(z.lazy(() => RuleOperationSchema)).optional(),
}).refine(
  (data) => {
    // If operator is "or" or "and", then ruleOperations is required and field/value/previousToolCallResultNodeId are not needed
    if (data.operator === 'or' || data.operator === 'and') {
      return data.ruleOperations && data.ruleOperations.length > 0;
    }
    // Otherwise, field, value, and previousToolCallResultNodeId are required
    return data.field && data.value && data.previousToolCallResultNodeId;
  },
  {
    message:
      'For "or" operator, ruleOperations is required. For other operators, field, value, and previousToolCallResultNodeId are required.',
  }
);

export const DecisionTreeRuleSchema = z.object({
  nextNodeId: z.string().min(1, 'Next node ID is required and cannot be empty'),
  ruleOperations: z
    .array(RuleOperationSchema)
    .min(1, 'Rules must have at least one rule operation'),
  comment: z.string().optional(),
});

export const DecisionTreeRuleNodeSchema = z.object({
  id: z.string().min(1, 'Node ID is required and cannot be empty'),
  type: z.literal(DecisionTreeNodeType.Rule, {
    errorMap: () => ({ message: 'Rule nodes must have type "rule"' }),
  }),
  rules: z.array(DecisionTreeRuleSchema).min(1, 'Rule nodes must have at least one rule'),
});
export type DecisionTreeRuleNode = z.infer<typeof DecisionTreeRuleNodeSchema>;

export const DecisionTreeDataCollectionNodeSchema = z.object({
  id: z.string().min(1, 'Node ID is required and cannot be empty'),
  type: z.literal(DecisionTreeNodeType.DataCollection, {
    errorMap: () => ({ message: 'Data collection nodes must have type "data_collection"' }),
  }),
  question: z.string().min(1, 'Question is required and cannot be empty'),
  nextNodeId: z.string().min(1, 'Next node ID is required and cannot be empty'),
  ehrQuestionId: z.string().optional(),
});
export type DecisionTreeDataCollectionNode = z.infer<typeof DecisionTreeDataCollectionNodeSchema>;

export const DecisionTreeEndNodeSchema = z.object({
  id: z.string().min(1, 'Node ID is required and cannot be empty'),
  type: z.literal(DecisionTreeNodeType.End, {
    errorMap: () => ({ message: 'End nodes must have type "end"' }),
  }),
  message: z.string().min(1, 'Message is required and cannot be empty'),
});
export type DecisionTreeEndNode = z.infer<typeof DecisionTreeEndNodeSchema>;

export const NodeSchema = z.discriminatedUnion('type', [
  DecisionTreeNodeSchema,
  DecisionTreeActionNodeSchema,
  DecisionTreeRuleNodeSchema,
  DecisionTreeDataCollectionNodeSchema,
  DecisionTreeEndNodeSchema,
]);
export type Node = z.infer<typeof NodeSchema>;

export const DecisionTreeSchema = z
  .record(z.string(), NodeSchema)
  // Commenting the start node out for now given we aren't enforcing the naming at the moment.
  // .refine(
  //   (tree) => {
  //     const nodeIds = Object.keys(tree);
  //     return nodeIds.includes('start');
  //   },
  //   {
  //     message:
  //       'Tree must contain a "start" node. Add a node with key "start" to begin the decision tree.',
  //   }
  // )
  .refine(
    (tree) => {
      const nodeIds = Object.keys(tree);
      return nodeIds.some((id) => {
        const node = tree[id];
        return isDefined(node) && node.type === DecisionTreeNodeType.End;
      });
    },
    {
      message:
        'Tree must contain at least one "end" node. Add a node with type "end" to complete the decision tree.',
    }
  )
  .refine(
    (tree) => {
      const nodeIds = Object.keys(tree);
      return nodeIds.every((nodeId) => {
        const node = tree[nodeId];
        return isDefined(node) && node.id === nodeId;
      });
    },
    {
      message:
        'Each node ID must match its key. For example, a node with key "start" must have id: "start".',
    }
  )
  .refine(
    (tree) => {
      const nodeIds = Object.keys(tree);

      // Define validation functions for each node type
      const nodeTypeValidators = {
        [DecisionTreeNodeType.Decision]: (node: Node) => {
          if (node.type === DecisionTreeNodeType.Decision && node.options) {
            for (const option of node.options) {
              if (!isDefined(tree[option.nextNodeId])) {
                return false;
              }
            }
          }
          return true;
        },
        [DecisionTreeNodeType.DataCollection]: (node: Node) => {
          if (
            node.type === DecisionTreeNodeType.DataCollection &&
            isDefined(node.nextNodeId) &&
            !isDefined(tree[node.nextNodeId])
          ) {
            return false;
          }
          return true;
        },
        [DecisionTreeNodeType.Rule]: (node: Node) => {
          if (node.type === DecisionTreeNodeType.Rule && node.rules) {
            for (const rule of node.rules) {
              if (!isDefined(tree[rule.nextNodeId])) {
                return false;
              }
            }
          }
          return true;
        },
        [DecisionTreeNodeType.Action]: () => true, // Action nodes don't have nextNodeId references
        [DecisionTreeNodeType.End]: () => true, // End nodes don't have nextNodeId references
      };

      for (const nodeId of nodeIds) {
        const node = tree[nodeId];
        const validator = nodeTypeValidators[node.type];

        if (!validator?.(node)) {
          return false;
        }
      }

      return true;
    },
    {
      message:
        'All referenced nextNodeIds must point to existing nodes. Check that all nextNodeId values match actual node keys in the tree.',
    }
  )
  .refine(
    (tree) => {
      const nodeIds = Object.keys(tree);
      return nodeIds.length !== 0;
    },
    {
      message: 'Tree cannot be empty. Add at least one node to create a valid decision tree.',
    }
  );
export type DecisionTreeType = z.infer<typeof DecisionTreeSchema>;

// New schema for the full decision tree structure with nodes, metadata, constants, and configuration
export const FullDecisionTreeSchema = z
  .object({
    nodes: z.record(z.string(), NodeSchema),
    metadata: z
      .object({
        name: z.string().min(1, 'Name is required'),
        ehrFormId: z.string().optional(),
        previsitFormTemplateId: z.string().optional(),
      })
      .optional(),
    constants: z
      .object({
        enums: z.record(z.string(), z.any()).optional(),
        practices: z.record(z.string(), z.any()).optional(),
        providers: z.record(z.string(), z.any()).optional(),
        appointment_types: z.record(z.string(), z.any()).optional(),
      })
      .optional(),
    configuration: z
      .object({
        initial_node_id: z.string().optional(),
        supports_test_mode: z.boolean().optional(),
      })
      .optional(),
  })
  .refine(
    (tree) => {
      const nodeIds = Object.keys(tree.nodes);
      return nodeIds.some((id) => {
        const node = tree.nodes[id];
        return isDefined(node) && node.type === DecisionTreeNodeType.End;
      });
    },
    {
      message:
        'Tree must contain at least one "end" node. Add a node with type "end" to complete the decision tree.',
    }
  )
  .refine(
    (tree) => {
      const nodeIds = Object.keys(tree.nodes);
      return nodeIds.every((nodeId) => {
        const node = tree.nodes[nodeId];
        return isDefined(node) && node.id === nodeId;
      });
    },
    {
      message:
        'Each node ID must match its key. For example, a node with key "start" must have id: "start".',
    }
  )
  .refine(
    (tree) => {
      const nodeIds = Object.keys(tree.nodes);

      const nodeTypeValidators = {
        [DecisionTreeNodeType.Decision]: (node: Node) => {
          if (node.type === DecisionTreeNodeType.Decision && node.options) {
            for (const option of node.options) {
              if (!isDefined(tree.nodes[option.nextNodeId])) {
                return false;
              }
            }
          }
          return true;
        },
        [DecisionTreeNodeType.DataCollection]: (node: Node) => {
          if (
            node.type === DecisionTreeNodeType.DataCollection &&
            isDefined(node.nextNodeId) &&
            !isDefined(tree.nodes[node.nextNodeId])
          ) {
            return false;
          }
          return true;
        },
        [DecisionTreeNodeType.Rule]: (node: Node) => {
          if (node.type === DecisionTreeNodeType.Rule && node.rules) {
            for (const rule of node.rules) {
              if (!isDefined(tree.nodes[rule.nextNodeId])) {
                return false;
              }
            }
          }
          return true;
        },
        [DecisionTreeNodeType.Action]: () => true, // Action nodes don't have nextNodeId references
        [DecisionTreeNodeType.End]: () => true, // End nodes don't have nextNodeId references
      };

      for (const nodeId of nodeIds) {
        const node = tree.nodes[nodeId];
        const validator = nodeTypeValidators[node.type];

        if (!validator?.(node)) {
          return false;
        }
      }

      return true;
    },
    {
      message:
        'All referenced nextNodeIds must point to existing nodes. Check that all nextNodeId values match actual node keys in the tree.',
    }
  )
  .refine(
    (tree) => {
      const nodeIds = Object.keys(tree.nodes);
      return nodeIds.length !== 0;
    },
    {
      message: 'Tree cannot be empty. Add at least one node to create a valid decision tree.',
    }
  );

export type FullDecisionTreeType = z.infer<typeof FullDecisionTreeSchema>;

// Legacy interface types for backward compatibility
export interface BaseDecisionTree {
  name: string;
  organizationId: string;
  createdByUserId: string | null;
  lastUpdatedByUserId: string | null;
  tree: DecisionTreeType;
  metadata: Record<string, any> | null;
}

export interface CreateValues extends NullablePartial<BaseDecisionTree> {}

export interface UpdateValues extends Partial<BaseDecisionTree> {}

export interface DecisionTree extends BaseItem, BaseDecisionTree {}

export interface FindAllOptions {
  name?: string;
  organizationIds?: string[];
}

// Helper function to create verbose error messages from Zod validation results
export const formatDecisionTreeValidationError = (
  jsonText: string | undefined,
  validationResult: z.SafeParseReturnType<
    z.infer<typeof DecisionTreeSchema>,
    z.infer<typeof DecisionTreeSchema>
  >
): string => {
  if (validationResult.success || !isDefined(jsonText)) {
    return '';
  }

  // Type guard to ensure we have the error case
  if (!validationResult.success) {
    const errors = validationResult.error.errors;
    const errorMessages: string[] = [];

    for (const error of errors) {
      let message = '';

      if (error.path.length > 0) {
        const fieldPath = error.path.join('.');
        message = `${fieldPath}: ${error.message}`;

        // Add more context for common error types
        if (error.code === 'invalid_type') {
          message += ` (expected ${error.expected}, received ${error.received})`;
        } else if (error.code === 'too_small') {
          message += ` (minimum ${error.minimum} required)`;
        } else if (error.code === 'invalid_enum_value') {
          message += ` (allowed values: ${error.options?.join(', ')})`;
        }
      } else {
        // For tree-level errors (like missing nodes), provide more context
        message = error.message;

        // Try to find the specific issue in the JSON
        if (message.includes('nextNodeId') && message.includes('existing nodes')) {
          try {
            const parsed = JSON.parse(jsonText);
            const availableNodes = new Set(Object.keys(parsed));
            const lines = jsonText.split('\n');

            // Find lines with invalid nextNodeId references
            for (let i = 0; i < lines.length; i++) {
              const line = lines[i];
              if (line.includes('nextNodeId')) {
                // Extract the nextNodeId value from this line
                const match = /"nextNodeId":\s*"([^"]+)"/.exec(line);
                if (match) {
                  const nodeId = match[1];
                  if (!availableNodes.has(nodeId)) {
                    message += ` (line ${i + 1}: "${nodeId}" does not exist)`;
                    break;
                  }
                }
              }
            }
          } catch (e) {
            if (error.path.length > 0) {
              const fieldPath = error.path.join('.');
              message = `${fieldPath}: ${error.message}`;

              // Add more context for common error types
              if (error.code === 'invalid_type') {
                message += ` (expected ${error.expected}, received ${error.received})`;
              } else if (error.code === 'too_small') {
                message += ` (minimum ${error.minimum} required)`;
              } else if (error.code === 'invalid_enum_value') {
                message += ` (allowed values: ${error.options?.join(', ')})`;
              }
            } else {
              // For tree-level errors (like missing nodes), provide more context
              message = error.message;

              // Try to find the specific issue in the JSON
              if (message.includes('nextNodeId') && message.includes('existing nodes')) {
                try {
                  const parsed = JSON.parse(jsonText);
                  const availableNodes = new Set(Object.keys(parsed));
                  const lines = jsonText.split('\n');

                  // Find lines with invalid nextNodeId references
                  for (let i = 0; i < lines.length; i++) {
                    const line = lines[i];
                    if (line.includes('nextNodeId')) {
                      // Extract the nextNodeId value from this line
                      const match = /"nextNodeId":\s*"([^"]+)"/.exec(line);
                      if (match) {
                        const nodeId = match[1];
                        if (!availableNodes.has(nodeId)) {
                          message += ` (line ${i + 1}: "${nodeId}" does not exist)`;
                          break;
                        }
                      }
                    }
                  }
                } catch (e) {
                  // Fallback to generic message if parsing fails
                  message += ' (check all nextNodeId references)';
                }
              }
            }

            errorMessages.push(message);
          }

          return errorMessages.join('\n');
        }
      }
    }
  }
  return '';
};
